// src/components/DigitalHuman/DigitalHumanChat.tsx
import {
    createCloudAvatar,
    IAvatarAudioFormat,
    IAvatarInitConfig,
    IOutboundSampleRate,
    TYVoiceChatMessage,
    TYVoiceChatMode,
    type CloudAvatar,
} from "lm-avatar-chat-sdk/cloud";
import {
    CSSProperties,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import {
    formatAvatarSessionError,
    isAvatarBusyError,
} from "../../utils/avatarSessionError";
import { TTS_TAIL_CONFIG } from "../../utils/constants";
import {
    concatInt16,
    padTtsOnset,
    silencePcmBuffer,
    takeInt16,
} from "../../utils/pcm";
import "./DigitalHumanChat.scss";

export interface DigitalHumanChatProps {
    className?: string;
    style?: CSSProperties;
    voiceMode?: TYVoiceChatMode;
    baseUrl: string;
    headers?: Record<string, string>;
    /** 挂载后自动拉起 /new_session 并启动数字人 */
    autoStart?: boolean;
    /** 是否显示内部「开启/关闭数字人」按钮（总会话由 App 管理时应关闭） */
    showLauncher?: boolean;
    onReady?: () => void;
    onError?: (error: {
        message?: string;
        terminate?: boolean;
        code?: string;
        busy?: boolean;
    }) => void;
    onMessage?: (msg: TYVoiceChatMessage) => void;
    onExit?: () => void;
    onExternalExit?: () => void;
    /** 本轮 TTS 已喂完并收口，数字人即将说完 */
    onPlaybackEnd?: () => void;
}

export interface DigitalHumanChatRef {
    start: () => void;
    exit: () => void;
    interrupt: () => void;
    toggleMute: () => void;
    isMuted: () => boolean;
    isReady: () => boolean;
    playTTSAudio: (audioData: ArrayBuffer) => boolean;
    finishTTSAudio: () => void;
    beginTTSTurn: () => void;
}

const DigitalHumanChat = forwardRef<DigitalHumanChatRef, DigitalHumanChatProps>(
    (props, ref) => {
        const {
            className,
            style,
            voiceMode = TYVoiceChatMode.tap2talk,
            baseUrl,
            headers = {},
            autoStart = false,
            showLauncher = true,
            onReady,
            onError,
            onMessage,
            onExit,
            onExternalExit,
            onPlaybackEnd,
        } = props;

        const cloudVideoRef = useRef<HTMLVideoElement>(null);
        const avatarRef = useRef<CloudAvatar | undefined>(undefined);
        const readyRef = useRef(false);
        const firstFrameRef = useRef(false);
        const speechReadyRef = useRef(false);
        const speechReadyTimerRef = useRef<number | null>(null);
        const startingRef = useRef(false);
        const startAbortRef = useRef<AbortController | null>(null);
        const pcmChunksRef = useRef<Int16Array[]>([]);
        const pcmSamplesRef = useRef(0);
        const pcmPrimedRef = useRef(false);
        const pcmFlushTimerRef = useRef<number | null>(null);
        const playQueueRef = useRef<Int16Array[]>([]);
        const playSamplesRef = useRef(0);
        const playPumpTimerRef = useRef<number | null>(null);
        const playEndingRef = useRef(false);
        const onReadyRef = useRef(onReady);
        const onErrorRef = useRef(onError);
        const onExitRef = useRef(onExit);
        const onPlaybackEndRef = useRef(onPlaybackEnd);
        onReadyRef.current = onReady;
        onErrorRef.current = onError;
        onExitRef.current = onExit;
        onPlaybackEndRef.current = onPlaybackEnd;
        const [muted, setMuted] = useState(false);
        const [isLoading, setIsLoading] = useState(false);
        const [errorMsg, setErrorMsg] = useState<string | null>(null);
        const [chatState, setChatState] = useState<"offline" | "online">(
            "offline",
        );

        // 防止 JSON.stringify 循环引用
        useEffect(() => {
            const originalStringify = JSON.stringify;
            JSON.stringify = function (value, replacer, space) {
                try {
                    return originalStringify.call(this, value, replacer, space);
                } catch (_) {
                    return "{}";
                }
            };
            return () => {
                JSON.stringify = originalStringify;
            };
        }, []);

        // 启动数字人（固定使用 /new_session）；startingRef 同步上锁，避免 Strict Mode 连打两次
        const startChat = async () => {
            if (startingRef.current || readyRef.current || avatarRef.current) {
                console.log("数字人已在启动或在线，跳过重复请求");
                return;
            }
            startingRef.current = true;
            firstFrameRef.current = false;
            speechReadyRef.current = false;
            readyRef.current = false;
            setIsLoading(true);
            setErrorMsg(null);

            const ac = new AbortController();
            startAbortRef.current = ac;
            const fullUrl = `${baseUrl}/new_session`;

            try {
                console.log(`[数字人] 获取会话参数: ${fullUrl}`);
                const response = await fetch(fullUrl, {
                    method: "GET",
                    headers: { "Content-Type": "application/json", ...headers },
                    signal: ac.signal,
                });
                const data = (await response.json().catch(() => ({}))) as {
                    success?: boolean;
                    message?: string;
                    error?: string;
                    code?: string;
                    rtcParams?: unknown;
                    sessionId?: string;
                };
                if (ac.signal.aborted) return;

                if (!response.ok || data.success === false) {
                    const raw =
                        data.error ||
                        data.message ||
                        `获取会话失败 (${response.status})`;
                    const friendly = formatAvatarSessionError(raw, data.code);
                    const startErr = new Error(friendly) as Error & {
                        code?: string;
                        busy?: boolean;
                    };
                    startErr.code = data.code;
                    startErr.busy = isAvatarBusyError(raw, data.code);
                    throw startErr;
                }

                const { rtcParams, sessionId } = data;
                if (!rtcParams || !sessionId) {
                    throw new Error("服务端返回数据不完整");
                }

                const container = cloudVideoRef.current;
                if (!container) {
                    throw new Error("数字人容器未就绪");
                }
                if (ac.signal.aborted) return;

                const initConfig = {
                    rootContainer: container,
                    sessionId,
                    ignoreAudioInput: true,
                    ...(rtcParams as object),
                } as unknown as IAvatarInitConfig;

                const avatar = createCloudAvatar(initConfig);

                avatarRef.current = avatar;

                const clearSpeechReadyTimer = () => {
                    if (speechReadyTimerRef.current) {
                        window.clearTimeout(speechReadyTimerRef.current);
                        speechReadyTimerRef.current = null;
                    }
                };

                const markFullyReady = () => {
                    if (
                        !firstFrameRef.current ||
                        !speechReadyRef.current ||
                        readyRef.current
                    ) {
                        return;
                    }
                    clearSpeechReadyTimer();
                    startingRef.current = false;
                    readyRef.current = true;
                    setChatState("online");
                    setIsLoading(false);
                    onReadyRef.current?.();
                    console.log("✅ 数字人已就绪，可接收语音播报");
                };

                avatar.onFirstFrameReceived(() => {
                    firstFrameRef.current = true;
                    console.log("✅ 数字人首帧渲染完成");
                    if (!speechReadyRef.current) {
                        speechReadyTimerRef.current = window.setTimeout(() => {
                            speechReadyTimerRef.current = null;
                            if (!speechReadyRef.current) {
                                speechReadyRef.current = true;
                                markFullyReady();
                            }
                        }, 1200);
                    }
                    markFullyReady();
                });

                avatar.onReadyToSpeech(() => {
                    console.log("🎤 数字人已就绪 (onReadyToSpeech)");
                    speechReadyRef.current = true;
                    markFullyReady();
                });

                avatar.onErrorReceived((err: any) => {
                    startingRef.current = false;
                    const friendly = formatAvatarSessionError(
                        err?.message,
                        err?.code,
                    );
                    setErrorMsg(friendly);
                    setIsLoading(false);
                    onErrorRef.current?.({
                        ...err,
                        message: friendly,
                        busy: isAvatarBusyError(err?.message, err?.code),
                    });
                    if (err.terminate) {
                        readyRef.current = false;
                        avatar.exit();
                        avatarRef.current = undefined;
                        setChatState("offline");
                        onExitRef.current?.();
                    }
                });

                avatar.onMessageReceived((msg: TYVoiceChatMessage) => {
                    onMessage?.(msg);
                });

                avatar.start({
                    mode: voiceMode,
                    outboundSampleRate:
                        TTS_TAIL_CONFIG.SAMPLE_RATE as IOutboundSampleRate,
                    avatarAudioFormat: IAvatarAudioFormat.s16,
                });
                console.log(`🚀 数字人已启动，模式: ${voiceMode}`);
            } catch (err) {
                if (
                    ac.signal.aborted ||
                    (err instanceof DOMException && err.name === "AbortError")
                ) {
                    startingRef.current = false;
                    return;
                }
                const raw = err instanceof Error ? err.message : String(err);
                const hasProp = <K extends string>(
                    o: unknown,
                    k: K,
                ): o is Record<K, unknown> =>
                    typeof o === "object" && o !== null && k in o;
                const code = hasProp(err, "code")
                    ? String((err as { code?: string }).code ?? "")
                    : "";
                const busy =
                    (hasProp(err, "busy") &&
                        Boolean((err as { busy?: boolean }).busy)) ||
                    isAvatarBusyError(raw, code);
                const msg = formatAvatarSessionError(raw, code);
                startingRef.current = false;
                setErrorMsg(msg);
                setIsLoading(false);
                onErrorRef.current?.({
                    message: msg,
                    code,
                    busy,
                    terminate: false,
                });
                console.error("[数字人] 启动失败:", msg);
            }
        };

        // 退出数字人。notifyParent=false 时由总会话层主动结束，避免回调环
        const exitChat = (notifyParent = true) => {
            startAbortRef.current?.abort();
            startAbortRef.current = null;
            startingRef.current = false;
            firstFrameRef.current = false;
            speechReadyRef.current = false;
            if (speechReadyTimerRef.current) {
                window.clearTimeout(speechReadyTimerRef.current);
                speechReadyTimerRef.current = null;
            }
            if (avatarRef.current) {
                avatarRef.current.exit();
                avatarRef.current = undefined;
                readyRef.current = false;
                pcmChunksRef.current = [];
                pcmSamplesRef.current = 0;
                pcmPrimedRef.current = false;
                playQueueRef.current = [];
                playSamplesRef.current = 0;
                playEndingRef.current = false;
                if (playPumpTimerRef.current) {
                    clearInterval(playPumpTimerRef.current);
                    playPumpTimerRef.current = null;
                }
                if (pcmFlushTimerRef.current) {
                    clearTimeout(pcmFlushTimerRef.current);
                    pcmFlushTimerRef.current = null;
                }
                setChatState("offline");
                onExitRef.current?.();
                if (notifyParent) {
                    onExternalExit?.();
                }
            }
            setIsLoading(false);
            setErrorMsg(null);
        };

        // 内部清理（仅用于页面关闭/刷新，不触发外部回调）
        const cleanupOnUnload = () => {
            if (avatarRef.current) {
                try {
                    avatarRef.current.exit();
                } catch (_) {
                    // 忽略关闭时的异常
                }
                avatarRef.current = undefined;
                // 状态会在组件卸载时重置，此处不强制更新
            }
        };

        // ----- 音频推送：先入本地队列，再匀速喂给 SDK，避免中间断流 -----
        const sendToSdk = (samples: Int16Array, end: boolean) => {
            const avatar = avatarRef.current as
                | {
                      pushAudioData?: (a: Int16Array, end?: boolean) => void;
                      feedAudioData?: (a: Int16Array, end?: boolean) => void;
                  }
                | undefined;
            if (!avatar) {
                console.warn("[数字人] 实例不存在，音频被丢弃");
                return;
            }
            if (samples.length === 0 && !end) return;
            try {
                if (typeof avatar.pushAudioData === "function") {
                    avatar.pushAudioData(samples, end);
                } else if (typeof avatar.feedAudioData === "function") {
                    avatar.feedAudioData(samples, end);
                } else {
                    console.warn(
                        "[数字人] 当前 SDK 不支持 pushAudioData 或 feedAudioData",
                    );
                }
            } catch (error) {
                console.error("[数字人] 音频推送异常:", error);
            }
        };

        const notifyPlaybackEnd = () => {
            onPlaybackEndRef.current?.();
        };

        const stopPlayPump = () => {
            if (playPumpTimerRef.current) {
                clearInterval(playPumpTimerRef.current);
                playPumpTimerRef.current = null;
            }
        };

        const pumpPlay = () => {
            if (playSamplesRef.current === 0) {
                if (playEndingRef.current) {
                    sendToSdk(new Int16Array(0), true);
                    playEndingRef.current = false;
                    stopPlayPump();
                    notifyPlaybackEnd();
                }
                return;
            }
            const slice = takeInt16(
                playQueueRef.current,
                TTS_TAIL_CONFIG.PUMP_SAMPLES,
            );
            playSamplesRef.current = Math.max(
                0,
                playSamplesRef.current - slice.length,
            );
            const isEnd = playEndingRef.current && playSamplesRef.current === 0;
            sendToSdk(slice, isEnd);
            if (isEnd) {
                playEndingRef.current = false;
                stopPlayPump();
                notifyPlaybackEnd();
            }
        };

        const startPlayPump = () => {
            if (playPumpTimerRef.current != null) return;
            playPumpTimerRef.current = window.setInterval(
                pumpPlay,
                TTS_TAIL_CONFIG.PUMP_INTERVAL_MS,
            );
            pumpPlay();
        };

        const enqueuePlay = (samples: Int16Array, end: boolean) => {
            if (samples.length > 0) {
                playQueueRef.current.push(samples);
                playSamplesRef.current += samples.length;
            }
            if (end) playEndingRef.current = true;
            startPlayPump();
        };

        const clearPcmFlushTimer = () => {
            if (pcmFlushTimerRef.current) {
                clearTimeout(pcmFlushTimerRef.current);
                pcmFlushTimerRef.current = null;
            }
        };

        const flushPcmQueue = (end: boolean) => {
            clearPcmFlushTimer();
            const chunks = pcmChunksRef.current;
            pcmChunksRef.current = [];
            pcmSamplesRef.current = 0;
            let merged = concatInt16(chunks);
            if (!pcmPrimedRef.current && merged.length > 0) {
                merged = padTtsOnset(
                    merged,
                    TTS_TAIL_CONFIG.SAMPLE_RATE,
                    TTS_TAIL_CONFIG.HEAD_SILENCE_MS,
                );
            }
            if (end) {
                const pad = new Int16Array(
                    silencePcmBuffer(
                        TTS_TAIL_CONFIG.SILENCE_MS,
                        TTS_TAIL_CONFIG.SAMPLE_RATE,
                    ),
                );
                merged = concatInt16(merged.length ? [merged, pad] : [pad]);
                pcmPrimedRef.current = false;
            }
            if (merged.length === 0 && !end) return;
            if (!end) pcmPrimedRef.current = true;
            enqueuePlay(merged, end);
        };

        const enqueuePcm = (audioData: ArrayBuffer) => {
            const even =
                audioData.byteLength % 2 === 0
                    ? audioData
                    : audioData.slice(0, audioData.byteLength - 1);
            if (even.byteLength < 2) return;
            const samples = new Int16Array(even.slice(0));
            pcmChunksRef.current.push(samples);
            pcmSamplesRef.current += samples.length;

            if (
                !pcmPrimedRef.current &&
                pcmSamplesRef.current < TTS_TAIL_CONFIG.PREBUFFER_SAMPLES
            ) {
                return;
            }
            if (!pcmPrimedRef.current) {
                flushPcmQueue(false);
                return;
            }
            clearPcmFlushTimer();
            pcmFlushTimerRef.current = window.setTimeout(() => {
                pcmFlushTimerRef.current = null;
                flushPcmQueue(false);
            }, TTS_TAIL_CONFIG.FLUSH_DEBOUNCE_MS);
        };

        useImperativeHandle(ref, () => ({
            start: startChat,
            exit: () => exitChat(false),
            interrupt: () => avatarRef.current?.interrupt(),
            toggleMute: () => {
                const avatar = avatarRef.current;
                if (!avatar) return;
                const newMuted = !muted;
                avatar.muteLocalMic(newMuted);
                setMuted(newMuted);
            },
            isMuted: () => muted,
            isReady: () => readyRef.current && !!avatarRef.current,
            playTTSAudio: (audioData: ArrayBuffer) => {
                if (!readyRef.current || !avatarRef.current) {
                    console.warn("[数字人] 未就绪，音频被丢弃");
                    return false;
                }
                enqueuePcm(audioData);
                return true;
            },
            finishTTSAudio: () => {
                if (!readyRef.current || !avatarRef.current) {
                    // 实例已丢时也要通知上层解锁，否则 avatarSpeaking 永久为 true
                    notifyPlaybackEnd();
                    return;
                }
                flushPcmQueue(true);
            },
            beginTTSTurn: () => {
                clearPcmFlushTimer();
                stopPlayPump();
                pcmChunksRef.current = [];
                pcmSamplesRef.current = 0;
                pcmPrimedRef.current = false;
                playQueueRef.current = [];
                playSamplesRef.current = 0;
                playEndingRef.current = false;
                const warm = new Int16Array(
                    silencePcmBuffer(240, TTS_TAIL_CONFIG.SAMPLE_RATE),
                );
                if (warm.length > 0) {
                    enqueuePlay(warm, false);
                }
            },
        }));

        // 自动启动 + 卸载清理合并，卸载时 abort 进行中的 /new_session，避免 Strict Mode 连打
        useEffect(() => {
            if (autoStart) {
                void startChat();
            }
            return () => {
                startAbortRef.current?.abort();
                startAbortRef.current = null;
                startingRef.current = false;
                firstFrameRef.current = false;
                speechReadyRef.current = false;
                if (speechReadyTimerRef.current) {
                    window.clearTimeout(speechReadyTimerRef.current);
                    speechReadyTimerRef.current = null;
                }
                if (avatarRef.current) {
                    try {
                        avatarRef.current.exit();
                    } catch {
                        /* ignore */
                    }
                    avatarRef.current = undefined;
                    readyRef.current = false;
                    onExitRef.current?.();
                }
            };
            // 仅在挂载/autoStart 变化时执行
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [autoStart]);

        // 页面刷新 / 关闭时主动退出（不触发外部回调，避免状态更新警告）
        useEffect(() => {
            const handleBeforeUnload = () => {
                cleanupOnUnload();
            };
            window.addEventListener("beforeunload", handleBeforeUnload);
            return () => {
                window.removeEventListener("beforeunload", handleBeforeUnload);
            };
        }, []);

        return (
            <div
                className={className}
                style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    backgroundColor: "#1a1a2e",
                    overflow: "hidden",
                    ...style,
                }}
            >
                <video
                    ref={cloudVideoRef}
                    muted
                    playsInline
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                    }}
                />

                {isLoading && (
                    <div
                        className="dh-loading"
                        role="status"
                        aria-live="polite"
                    >
                        <div className="dh-loading-core">
                            <span className="dh-loading-ring dh-loading-ring--outer" />
                            <span className="dh-loading-ring dh-loading-ring--mid" />
                            <span className="dh-loading-ring dh-loading-ring--inner" />
                            <span className="dh-loading-hex" />
                            <span className="dh-loading-scan" />
                        </div>
                        <p className="dh-loading-text">
                            加载中
                            <span className="dh-loading-dots" aria-hidden>
                                <span>.</span>
                                <span>.</span>
                                <span>.</span>
                            </span>
                        </p>
                    </div>
                )}

                {errorMsg && !isLoading && (
                    <div className="dh-error" role="alert">
                        <p className="dh-error-title">数字人暂时无法接入</p>
                        <p className="dh-error-text">{errorMsg}</p>
                        <button
                            type="button"
                            className="dh-error-retry"
                            onClick={() => void startChat()}
                        >
                            稍后重试
                        </button>
                    </div>
                )}

                {showLauncher && !isLoading && (
                    <div
                        style={{
                            position: "absolute",
                            bottom: 20,
                            left: "50%",
                            transform: "translateX(-50%)",
                            display: "flex",
                            gap: "12px",
                            zIndex: 10,
                        }}
                    >
                        {chatState === "offline" ? (
                            <button
                                onClick={() => void startChat()}
                                style={{
                                    padding: "10px 30px",
                                    fontSize: "16px",
                                    borderRadius: "8px",
                                    border: "none",
                                    background: "#1890ff",
                                    color: "#fff",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                }}
                            >
                                ▶ 开启数字人
                            </button>
                        ) : (
                            <button
                                onClick={() => exitChat(true)}
                                style={{
                                    padding: "10px 30px",
                                    fontSize: "16px",
                                    borderRadius: "8px",
                                    border: "none",
                                    background: "#ff4d4f",
                                    color: "#fff",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                                }}
                            >
                                ⏹ 关闭数字人
                            </button>
                        )}
                    </div>
                )}

                {chatState === "online" && !isLoading && (
                    <div
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 16,
                            background: "rgba(0,200,100,0.8)",
                            color: "#fff",
                            padding: "4px 12px",
                            borderRadius: 20,
                            fontSize: "12px",
                            zIndex: 10,
                        }}
                    >
                        ● 金小卫在线
                    </div>
                )}
            </div>
        );
    },
);

DigitalHumanChat.displayName = "DigitalHumanChat";
export default DigitalHumanChat;
